/** Pure clock maths for the live countdown banner (display only). */

export type CountdownStage = 'normal' | 'warning' | 'critical' | 'done';

export const WARNING_MS = 600000;
export const CRITICAL_MS = 60000;
export const POLL_MS = 60000;
export const TICK_MS = 1000;
export const DEFAULT_COUNTDOWN_LABEL = 'Submissions close in';

const pad = (n: number) => String(n).padStart(2, '0');

/** How far the server clock is ahead of this browser's clock. */
export function serverOffsetMs(serverTimeIso: string, receivedAtMs: number): number {
  const server = Date.parse(serverTimeIso);
  return Number.isNaN(server) ? 0 : server - receivedAtMs;
}

export function remainingMs(endsAtIso: string, nowMs: number, offsetMs: number): number {
  const ends = Date.parse(endsAtIso);
  if (Number.isNaN(ends)) return 0;
  return Math.max(0, ends - (nowMs + offsetMs));
}

export function formatCountdown(ms: number): string {
  // Round up so the clock never reads 00:00:00 while time is still left.
  const total = Math.ceil(Math.max(0, ms) / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const clock = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  return days > 0 ? `${days}d ${clock}` : clock;
}

export function countdownStage(ms: number): CountdownStage {
  if (ms <= 0) return 'done';
  if (ms < CRITICAL_MS) return 'critical';
  if (ms <= WARNING_MS) return 'warning';
  return 'normal';
}

/**
 * "2026-10-25T16:00" (browser local) -> "2026-10-25T16:00:00-05:00".
 * The string is built from the normalised Date, so a wall time inside a DST
 * spring-forward gap (e.g. 02:30 on the change day) comes back as the real
 * instant (03:30) with its matching offset.
 */
export function toOffsetIso(localValue: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localValue);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const date = new Date(y, mo - 1, d, h, mi, 0);
  if (Number.isNaN(date.getTime())) return null;
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${toLocalInputValue(date.toISOString())}:00${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

/** ISO instant -> "YYYY-MM-DDTHH:MM" in the browser's zone, for <input type="datetime-local">. */
export function toLocalInputValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
